import { Badge } from "@/components/badge";
import { Button } from "@/components/button";
import { Experiment, ExperimentTag } from "@/http/api-types";
import { formatUTCDate } from "@/lib/utils";
import { ArrowLeft, Pencil, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ExperimentTagsEditor } from "./ExperimentTags";
import { getExperimentStatus, getExperimentStatusLabel, getExperimentStatusVariant } from "./experiment-status";

type ExperimentHeaderProps = {
  experiment: Experiment;
  isEditMode: boolean;
  canEdit?: boolean;
  onEditClick: () => void;
  onCancelClick: () => void;
  onTagsSaved: (tags: ExperimentTag[]) => void;
};

export function ExperimentHeader({
  experiment,
  isEditMode,
  canEdit = true,
  onEditClick,
  onCancelClick,
  onTagsSaved,
}: ExperimentHeaderProps) {
  const navigate = useNavigate();
  const status = getExperimentStatus(experiment);

  return (
    <div className="flex items-start justify-between mb-6">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">{experiment.name}</h1>
          <Badge variant={getExperimentStatusVariant(status)}>{getExperimentStatusLabel(status)}</Badge>
        </div>
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span>{formatUTCDate(experiment.start_date)}</span>
          <span>→</span>
          <span>{formatUTCDate(experiment.end_date)}</span>
        </div>
        <ExperimentTagsEditor experimentId={experiment.id} tags={experiment.tags} onTagsSaved={onTagsSaved} />
      </div>

      <div className="flex items-center gap-2">
        <Button variant="outline" onClick={() => navigate("/experiments")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        {isEditMode ? (
          <Button variant="outline" onClick={onCancelClick}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
        ) : canEdit ? (
          <Button onClick={onEditClick}>
            <Pencil className="h-4 w-4 mr-2" />
            Edit
          </Button>
        ) : null}
      </div>
    </div>
  );
}
